import {
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Select,
  Slider,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import useCollections from "../hooks/useCollections";
import useEventsLastChange from "../hooks/useEventsLastChange";
import useFlowDetectionsLastRecord from "../hooks/useFlowDetectionsLastRecord";
import useStoresData from "../hooks/useStoresData";
import useZones from "../hooks/useZones";

const { Text } = Typography;

const getItemOptions = (items, selectedItems) => {
  if (!items) return [];

  return items.map((item) => ({
    ...item,
    disabled: selectedItems?.includes("all") && item.value !== "all",
  }));
};

export default function Filters({
  isMainDataLoading = false,
  filters = ["data", "periodo", "zonas", "colecoes", "lojas"],
  selectedDate,
  setSelectedDate,
  selectedPeriod,
  setSelectedPeriod,
  selectedZones,
  setSelectedZones,
  selectedCollections,
  setSelectedCollections,
  selectedStores,
  setSelectedStores,
  dateRange = false,
  selectedDateRange,
  setSelectedDateRange,
  page = "flow_detections",
}) {
  const [localDate, setLocalDate] = useState(selectedDate);
  const [localDateRange, setLocalDateRange] = useState(selectedDateRange);
  const [localPeriod, setLocalPeriod] = useState(selectedPeriod);
  const [localZones, setLocalZones] = useState(selectedZones);
  const [localCollections, setLocalCollections] = useState(selectedCollections);
  const [localStores, setLocalStores] = useState(selectedStores);

  const { data: lastRecord, isPending: isLastRecordPending } =
    useFlowDetectionsLastRecord({ enabled: page === "flow_detections" });
  const { data: zones, isLoading: isZonesLoading } = useZones({
    active_only: true,
  });

  const { data: lastChange, isPending: isLastChangePending } =
    useEventsLastChange({ enabled: page === "collections_change" });
  const { data: collections, isLoading: isCollectionsLoading } =
    useCollections();
  const { data: stores, isLoading: isStoresLoading } = useStoresData({
    enabled: page === "revenue",
  });

  const loading =
    (isLastRecordPending && isZonesLoading) ||
    isCollectionsLoading ||
    isLastChangePending ||
    isStoresLoading;

  useEffect(() => {
    if (!lastChange && !lastRecord) return;

    if (page === "collections_change") {
      if (!isLastChangePending && lastChange?.date) {
        const formattedDate = dayjs(lastChange.date).format("YYYY-MM");
        setLocalDate(formattedDate);
        setSelectedDate(formattedDate);
      }
    } else if (page !== "revenue") {
      if (!isLastRecordPending && lastRecord?.date) {
        const formattedDate = dayjs(lastRecord.date).format("YYYY-MM-DD");

        if (dateRange) {
          const yearMonth = dayjs(lastRecord.date).format("YYYY-MM");
          setLocalDateRange([yearMonth, yearMonth]);
          setSelectedDateRange([yearMonth, yearMonth]);
        } else {
          setLocalDate(formattedDate);
          setSelectedDate(formattedDate);
        }
      }
    }
  }, [
    lastRecord,
    dateRange,
    isLastRecordPending,
    setSelectedDate,
    setSelectedDateRange,
    lastChange,
    isLastChangePending,
    page,
  ]);

  const getTagPlaceholder = (selectedItems, allItems) => {
    if (
      selectedItems?.length === allItems?.length - 1 ||
      selectedItems?.includes("all")
    ) {
      return "Todos";
    } else if (selectedItems?.length === 1) {
      return "1 selecionado";
    } else {
      return `${selectedItems?.length} selecionados`;
    }
  };

  const handleSelectionChange = (selectedValues, setter) => {
    if (selectedValues.includes("all")) {
      setter(["all"]);
    } else if (selectedValues.length === 0) {
      setter([]);
    } else {
      setter(selectedValues);
    }
  };

  const handleDateChange = (date) => {
    if (dateRange) {
      if (page === "revenue") {
        setLocalDateRange(
          date
            ? [date[0].format("YYYY-MM-DD"), date[1].format("YYYY-MM-DD")]
            : [
                dayjs().subtract(7, "day").format("YYYY-MM-DD"),
                dayjs().format("YYYY-MM-DD"),
              ]
        );
      } else {
        setLocalDateRange(
          date ? [date[0].format("YYYY-MM"), date[1].format("YYYY-MM")] : null
        );
      }
    } else {
      setLocalDate(date ? date.format("YYYY-MM-DD") : null);
    }
  };

  const handleApplyFilters = () => {
    // iterate over the filter[] prop and only apply the filters that are in the prop
    const filterActions = {
      data: () =>
        dateRange
          ? localDateRange
            ? setSelectedDateRange([localDateRange[0], localDateRange[1]])
            : setSelectedDateRange(null)
          : setSelectedDate(localDate),
      periodo: () => setSelectedPeriod(localPeriod),
      zonas: () => setSelectedZones(localZones),
      colecoes: () => setSelectedCollections(localCollections),
      lojas: () => setSelectedStores(localStores),
    };
    filters.forEach((filter) => filterActions[filter]?.());
  };

  return (
    <Card
      title="Aplique filtros para uma análise mais detalhada"
      loading={loading}
      style={{
        width: "90%",
        backgroundColor: "#d0d6e2",
      }}>
      <Row
        justify="space-between"
        gutter={[16, 16]}
        style={{
          minHeight: "75px",
        }}>
        {filters.includes("data") && (
          <Col>
            <Text strong>Data:</Text>
            <br />
            {dateRange ? (
              <DatePicker.RangePicker
                format={page === "revenue" ? "DD/MM/YYYY" : "YYYY/MM"}
                picker={page === "revenue" ? "date" : "month"}
                style={{ width: "250px" }}
                placeholder={["Data inicial", "Data final"]}
                value={[
                  localDateRange[0] ? dayjs(localDateRange[0]) : null,
                  localDateRange[1] ? dayjs(localDateRange[1]) : null,
                ]}
                onChange={handleDateChange}
              />
            ) : (
              <DatePicker
                style={{ width: "200px" }}
                picker={page === "collections_change" ? "month" : "date"}
                placeholder={
                  page === "collections_change"
                    ? "Selecione um mês"
                    : "Selecione uma data"
                }
                value={localDate ? dayjs(localDate) : null}
                onChange={handleDateChange}
                format={
                  page === "collections_change" ? "YYYY/MM" : "DD/MM/YYYY"
                }
              />
            )}
          </Col>
        )}
        {filters.includes("periodo") && (
          <Col>
            <Text strong>Período (h):</Text>
            <br />
            <Slider
              range
              value={localPeriod}
              onChange={setLocalPeriod}
              min={9}
              max={19}
              marks={{
                9: "9",
                19: "19",
              }}
              style={{ width: "200px" }}
              styles={{
                track: { backgroundColor: "black" },
              }}
            />
          </Col>
        )}
        {filters.includes("zonas") && (
          <Col>
            <Text strong>Zonas:</Text>
            <br />
            <Select
              mode="multiple"
              style={{ width: "200px" }}
              placeholder="Selecione uma zona"
              allowClear
              maxTagPlaceholder={() => getTagPlaceholder(localZones, zones)}
              value={localZones}
              onChange={(values) =>
                handleSelectionChange(values, setLocalZones)
              }
              maxTagCount={0}
              options={getItemOptions(zones, localZones)}
            />
          </Col>
        )}
        {filters.includes("colecoes") && (
          <Col>
            <Text strong>Coleção:</Text>
            <br />
            <Select
              mode="multiple"
              style={{ width: "200px" }}
              placeholder="Selecione uma coleção"
              allowClear
              maxTagPlaceholder={() =>
                getTagPlaceholder(localCollections, collections)
              }
              value={localCollections}
              onChange={(values) =>
                handleSelectionChange(values, setLocalCollections)
              }
              maxTagCount={0}
              options={getItemOptions(collections, localCollections)}
            />
          </Col>
        )}
        {filters.includes("lojas") && (
          <Col>
            <Text strong>Loja:</Text>
            <br />
            <Select
              mode="multiple"
              style={{ width: "200px" }}
              placeholder="Selecione uma loja"
              allowClear
              maxTagPlaceholder={() => getTagPlaceholder(localStores, stores)}
              value={localStores}
              onChange={(values) =>
                handleSelectionChange(values, setLocalStores)
              }
              maxTagCount={0}
              options={getItemOptions(stores, localStores)}
            />
          </Col>
        )}
        <Col
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}>
          <Button
            type="primary"
            onClick={handleApplyFilters}
            loading={isMainDataLoading}>
            Aplicar filtros
          </Button>
        </Col>
      </Row>
    </Card>
  );
}
